/**
 * Workflow Swimlane Visualization
 *
 * Displays workflows as horizontal swimlanes across lifecycle phases.
 * Inspired by BPMN swimlane diagrams with FERP's 5-phase lifecycle.
 *
 * Visual Features:
 * - Horizontal lanes for each lifecycle phase (DESIGN → OPTIMIZE)
 * - Workflow instances shown as cards flowing through lanes
 * - Connection lines showing dependencies
 * - Real-time status updates
 * - Life-critical workflows highlighted
 */

import { useMemo } from 'react';
import {
  Paper,
  Stack,
  Group,
  Text,
  Badge,
  ThemeIcon,
  ScrollArea,
  Card,
  Progress,
  Tooltip,
  Box,
  Skeleton,
  Alert,
} from '@mantine/core';
import {
  IconArrowRight,
  IconCircuitDiode,
  IconSettings,
  IconPlayerPlay,
  IconChartBar,
  IconShield,
  IconAlertTriangle,
  IconCheck,
  IconClock,
  IconX,
} from '@tabler/icons-react';

import { useTenantId } from '@/auth';
import { useListWorkflowsQuery } from '@/generated/graphql';

type LifecyclePhase = 'DESIGN' | 'CONFIGURE' | 'EXECUTE' | 'MONITOR' | 'OPTIMIZE';

const PHASE_CONFIG: Record<LifecyclePhase, { label: string; color: string; icon: typeof IconCircuitDiode }> = {
  DESIGN: { label: 'Design', color: 'blue', icon: IconCircuitDiode },
  CONFIGURE: { label: 'Configure', color: 'grape', icon: IconSettings },
  EXECUTE: { label: 'Execute', color: 'cyan', icon: IconPlayerPlay },
  MONITOR: { label: 'Monitor', color: 'orange', icon: IconChartBar },
  OPTIMIZE: { label: 'Optimize', color: 'red', icon: IconShield },
};

const PHASES: LifecyclePhase[] = ['DESIGN', 'CONFIGURE', 'EXECUTE', 'MONITOR', 'OPTIMIZE'];

interface WorkflowCardProps {
  name: string;
  status: 'active' | 'pending' | 'completed' | 'failed';
  progress: number;
  lifeCritical?: boolean;
}

function WorkflowCard({ name, status, progress, lifeCritical }: WorkflowCardProps) {
  const statusConfig = {
    active: { color: 'blue', icon: IconPlayerPlay, label: 'Active' },
    pending: { color: 'yellow', icon: IconClock, label: 'Pending' },
    completed: { color: 'green', icon: IconCheck, label: 'Completed' },
    failed: { color: 'red', icon: IconX, label: 'Failed' },
  }[status];

  const StatusIcon = statusConfig.icon;

  return (
    <Card
      withBorder
      padding="xs"
      style={{
        minWidth: 200,
        maxWidth: 200,
        borderLeftWidth: lifeCritical ? 3 : 1,
        borderLeftColor: lifeCritical ? 'var(--mantine-color-red-6)' : undefined,
        transition: 'transform 0.2s, box-shadow 0.2s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = 'var(--mantine-shadow-md)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <Stack gap="xs">
        <Group justify="space-between" gap="xs">
          <Text size="sm" fw={600} lineClamp={1} style={{ flex: 1 }}>
            {name}
          </Text>
          <Tooltip label={statusConfig.label}>
            <ThemeIcon size="xs" c={statusConfig.color} variant="light" radius="xl">
              <StatusIcon size={10} />
            </ThemeIcon>
          </Tooltip>
        </Group>

        <Progress value={progress} size="xs" c={statusConfig.color} />

        <Group justify="space-between" gap={4}>
          <Text size="xs" c="dimmed">
            {progress}%
          </Text>
          {lifeCritical && (
            <Badge size="xs" c="red" variant="light">
              <Group gap={2}>
                <IconAlertTriangle size={8} />
                <span>Critical</span>
              </Group>
            </Badge>
          )}
        </Group>
      </Stack>
    </Card>
  );
}

interface SwimlaneLaneProps {
  phase: LifecyclePhase;
  workflows: WorkflowCardProps[];
}

function SwimlaneLane({ phase, workflows }: SwimlaneLaneProps) {
  const config = PHASE_CONFIG[phase];
  const PhaseIcon = config.icon;

  return (
    <Paper p="md" withBorder>
      {/* Lane Header */}
      <Group mb="md" justify="space-between">
        <Group gap="xs">
          <ThemeIcon c={config.color} variant="filled" size="md">
            <PhaseIcon size={16} />
          </ThemeIcon>
          <div>
            <Text size="sm" fw={600} tt="uppercase">
              {config.label}
            </Text>
            <Text size="xs" c="dimmed">
              {workflows.length} workflow{workflows.length !== 1 ? 's' : ''}
            </Text>
          </div>
        </Group>

        <IconArrowRight size={20} color="var(--mantine-color-dimmed)" />
      </Group>

      {/* Workflow Cards */}
      <ScrollArea type="auto">
        <Group gap="md" wrap="nowrap">
          {workflows.length === 0 ? (
            <Card withBorder padding="lg" style={{ minWidth: 200, opacity: 0.5 }}>
              <Text size="sm" c="dimmed" ta="center">
                No workflows in this phase
              </Text>
            </Card>
          ) : (
            workflows.map((workflow, idx) => (
              <WorkflowCard key={idx} {...workflow} />
            ))
          )}
        </Group>
      </ScrollArea>
    </Paper>
  );
}

export function WorkflowSwimlane() {
  const tenantId = useTenantId();

  const { data, loading, error } = useListWorkflowsQuery({
    variables: { limit: 100 },
    skip: !tenantId,
  });

  // Mock data for demonstration - replace with real workflow data mapping
  const workflowsByPhase = useMemo(() => {
    const workflows = data?.listWorkflows?.items ?? [];

    // For now, distribute workflows across phases for visualization
    // In production, workflows would have a 'currentPhase' field
    return {
      DESIGN: [
        { name: 'Patient Admission', status: 'active' as const, progress: 25, lifeCritical: true },
        { name: 'Treatment Plan', status: 'pending' as const, progress: 0, lifeCritical: true },
      ],
      CONFIGURE: [
        { name: 'Patient Admission', status: 'active' as const, progress: 60, lifeCritical: true },
        { name: 'Medication Order', status: 'pending' as const, progress: 10 },
      ],
      EXECUTE: [
        { name: 'Patient Admission', status: 'active' as const, progress: 85, lifeCritical: true },
        { name: 'Lab Test', status: 'active' as const, progress: 45 },
        { name: 'Discharge Planning', status: 'pending' as const, progress: 5 },
      ],
      MONITOR: [
        { name: 'Patient Admission', status: 'active' as const, progress: 95, lifeCritical: true },
        { name: 'Lab Test', status: 'completed' as const, progress: 100 },
      ],
      OPTIMIZE: [
        { name: 'Patient Admission', status: 'completed' as const, progress: 100, lifeCritical: true },
      ],
    };
  }, [data]);

  if (loading) {
    return (
      <Stack gap="md">
        {PHASES.map((phase) => (
          <Skeleton key={phase} height={120} />
        ))}
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert c="red" title="Failed to Load Workflows" icon={<IconAlertTriangle size={16} />}>
        {error.message}
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      {/* Connection Line Visual - Shows flow from left to right */}
      <Box
        style={{
          position: 'relative',
          height: 4,
          background: 'linear-gradient(to right, var(--mantine-color-blue-2), var(--mantine-color-grape-2), var(--mantine-color-cyan-2), var(--mantine-color-orange-2), var(--mantine-color-red-2))',
          borderRadius: 2,
        }}
      />

      {/* Swimlane Lanes */}
      {PHASES.map((phase) => (
        <SwimlaneLane key={phase} phase={phase} workflows={workflowsByPhase[phase]} />
      ))}

      {/* Legend */}
      <Paper p="sm" withBorder bg="gray.0">
        <Group justify="center" gap="xl">
          <Group gap="xs">
            <ThemeIcon size="xs" c="blue" variant="light" radius="xl">
              <IconPlayerPlay size={8} />
            </ThemeIcon>
            <Text size="xs" c="dimmed">Active</Text>
          </Group>
          <Group gap="xs">
            <ThemeIcon size="xs" c="yellow" variant="light" radius="xl">
              <IconClock size={8} />
            </ThemeIcon>
            <Text size="xs" c="dimmed">Pending</Text>
          </Group>
          <Group gap="xs">
            <ThemeIcon size="xs" c="green" variant="light" radius="xl">
              <IconCheck size={8} />
            </ThemeIcon>
            <Text size="xs" c="dimmed">Completed</Text>
          </Group>
          <Group gap="xs">
            <Box w={12} h={12} style={{ borderLeft: '3px solid var(--mantine-color-red-6)' }} />
            <Text size="xs" c="dimmed">Life-Critical</Text>
          </Group>
        </Group>
      </Paper>
    </Stack>
  );
}

export default WorkflowSwimlane;
